import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LongPage, TocEntry } from "./SideNav";
import { Bullet, Bullets, Mail, PageHeader, Section } from "./ui";
import { CONTACT_EMAIL } from "./constants";

/*
 * The short Security page, in both languages. The full technical account is
 * SecurityTechnicalPage at /security/technical, English only; each block here
 * links to the section there that backs it up. Keep the two in step: every
 * claim here restates something the technical page says.
 */

const TECHNICAL = "/security/technical";

// Block id on this page -> section id on the technical page.
const BLOCKS: Array<{ id: string; technical: string }> = [
  { id: "identities", technical: "pseudonymization" },
  { id: "access", technical: "access-control" },
  { id: "encrypted", technical: "storage" },
  { id: "login", technical: "accounts" },
  { id: "detection", technical: "detection" },
  { id: "audit", technical: "audit-trail" },
  { id: "retention", technical: "storage" },
];

const ReadMore: React.FC<{ to: string }> = ({ to }) => {
  const { t } = useTranslation("securitySummary");
  return (
    <p className="mt-3 text-sm">
      <Link to={`${TECHNICAL}#${to}`} className="text-accent underline underline-offset-2">
        {t("readMore")} →
      </Link>
    </p>
  );
};

export const SecuritySummaryPage: React.FC = () => {
  const { t } = useTranslation("securitySummary");

  const toc: TocEntry[] = [
    ...BLOCKS.map((b) => ({ id: b.id, label: t(`${b.id}.title`) })),
    { id: "limitations", label: t("limitations.title") },
    { id: "report", label: t("report.title") },
  ];

  return (
    <LongPage
      toc={toc}
      intro={
        <PageHeader
          kicker={t("header.kicker")}
          title={t("header.title")}
          lead={
            <p>
              {t("header.leadA")}
              <Link to={TECHNICAL} className="text-accent underline underline-offset-2">
                {t("header.leadLink")}
              </Link>
              {t("header.leadB")}
            </p>
          }
        />
      }
    >
      <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-12 [&>section]:pb-12 [&>section:last-child]:pb-0">
        {BLOCKS.map((b) => (
          <Section key={b.id} id={b.id} title={t(`${b.id}.title`)}>
            <p>{t(`${b.id}.body`)}</p>
            <ReadMore to={b.technical} />
          </Section>
        ))}

        <Section id="limitations" title={t("limitations.title")}>
          <p>{t("limitations.intro")}</p>
          <Bullets>
            <Bullet>
              <strong>{t("limitations.anonymousStrong")}</strong>
              {t("limitations.anonymousRest")}
            </Bullet>
            <Bullet>
              <strong>{t("limitations.operatorsStrong")}</strong>
              {t("limitations.operatorsRest")}
            </Bullet>
            <Bullet>
              <strong>{t("limitations.keysStrong")}</strong>
              {t("limitations.keysRest")}
            </Bullet>
            <Bullet>
              <strong>{t("limitations.auditStrong")}</strong>
              {t("limitations.auditRest")}
            </Bullet>
          </Bullets>
          <p>
            {t("limitations.fullListA")}
            <Link to={`${TECHNICAL}#review`} className="text-accent underline underline-offset-2">
              {t("limitations.fullListLink")}
            </Link>
            {t("limitations.fullListB")}
          </p>
        </Section>

        <Section id="report" title={t("report.title")}>
          <p>
            {t("report.bodyA")}
            <Mail address={CONTACT_EMAIL} subject="Security report" />
            {t("report.bodyB")}
          </p>
        </Section>
      </div>
    </LongPage>
  );
};
